// frame: OhKgp @ ui/dogdisk-user.pen
// spec: docs/ui/user-page-flows.md §2.3 OhKgp 页面-用户主页
//
// 公开用户主页(免登录可访问):/u/:userId
//   - 入口:agent 推广 / 资源包分享归属 / 自己头像跳"我的公开主页"
//   - 数据源:v_profile_public(只暴露 username/avatar/bio/stats;public_profile_enabled=false 的用户自动过滤)
//   - 数据源:v_resource_pack_public_meta 查公开 pack 列表 + v_article_feed 查已发布文章
//   - 关注 / 订阅 按钮级守卫:未登录 → /auth/login?next=/u/:userId
//
// 2026-06-02(spec §4.5):公开文件发现移除 — public_file_count → published_article_count,
// 「公开文件」tab 改为「文章」tab(v_article_feed?user_id=eq.<uuid>)。

import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Package } from 'lucide-react'
import { Spinner, getSupabase, useCurrentUser, cn } from '@dogdisk/shared'
import { MobileScreen, TopBar } from '../components/mobile-screen'
import { ReportModal } from '../components/modals/report-modal'
import { ArticleCard } from '../components/article-card'
import { Pagination } from '../components/pagination'
import { FEED_COLS, type ArticleFeedItem } from '../_lib/article-api'
import { NotFoundView } from './errors/not-found'
import { resourceUrl } from '../_lib/resource-node-pref'

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

// 公开资源包 / 文章列表分页页大小(对齐论坛 LNMX9)。
const PUBLIC_LIST_PAGE_SIZE = 20

type PublicProfile = {
  user_id: string
  username: string | null
  nickname: string | null
  avatar_url: string | null
  bio: string | null
  created_at: string
  public_pack_count: number
  published_article_count: number
  subscriber_count: number
}

type PublicPack = {
  id: string
  name: string
  cover_thumb_url_feed: string | null
  price_coin: number | null
  like_count: number
  subscriber_count: number
}

function usePublicProfile(userId: string) {
  return useQuery({
    queryKey: ['public-profile', userId] as const,
    enabled: UUID_RE.test(userId),
    queryFn: async (): Promise<PublicProfile | null> => {
      const sb = getSupabase()
      const { data, error } = await sb
        .from('v_profile_public')
        .select(
          'user_id, username, nickname, avatar_url, bio, created_at, public_pack_count, published_article_count, subscriber_count',
        )
        .eq('user_id', userId)
        .maybeSingle()
      if (error) {
        // View 未落地或 user 不存在 / public_profile_enabled=false → 视为 404
        return null
      }
      return (data as PublicProfile) ?? null
    },
  })
}

function usePublicPacks(userId: string, page: number, enabled: boolean) {
  return useQuery({
    queryKey: ['public-packs', userId, page] as const,
    enabled,
    queryFn: async (): Promise<PublicPack[]> => {
      const sb = getSupabase()
      const from = (page - 1) * PUBLIC_LIST_PAGE_SIZE
      const { data, error } = await sb
        .from('v_resource_pack_public_meta')
        .select('id, name, cover_thumb_url_feed, price_coin, like_count, subscriber_count')
        .eq('integrator_id', userId)
        .order('created_at', { ascending: false })
        .range(from, from + PUBLIC_LIST_PAGE_SIZE - 1)
      if (error) return []
      return (data as PublicPack[]) ?? []
    },
  })
}

function usePublicArticles(userId: string, page: number, enabled: boolean) {
  return useQuery({
    queryKey: ['public-articles', userId, page] as const,
    enabled,
    queryFn: async (): Promise<ArticleFeedItem[]> => {
      const sb = getSupabase()
      const from = (page - 1) * PUBLIC_LIST_PAGE_SIZE
      // v_article_feed 仅出 status='published';按作者筛(?user_id=eq.<uuid>)
      const { data, error } = await sb
        .from('v_article_feed')
        .select(FEED_COLS)
        .eq('user_id', userId)
        .order('published_at', { ascending: false })
        .order('id', { ascending: false })
        .range(from, from + PUBLIC_LIST_PAGE_SIZE - 1)
      if (error) return []
      return (data as ArticleFeedItem[]) ?? []
    },
  })
}

export function UserProfilePage() {
  const { userId = '' } = useParams<{ userId: string }>()
  const navigate = useNavigate()
  const { status } = useCurrentUser()
  const isAuth = status === 'authenticated'
  const [tab, setTab] = useState<'packs' | 'articles'>('packs')
  // 两个列表各自独立分页(互不影响)。
  const [packsPage, setPacksPage] = useState(1)
  const [articlesPage, setArticlesPage] = useState(1)
  // D6 DY9nl 举报 modal 控制
  const [reportOpen, setReportOpen] = useState(false)

  const scrollListTop = () => window.scrollTo({ top: 0 })

  if (!UUID_RE.test(userId)) {
    return <NotFoundView reason="invalid_user_id" />
  }

  const { data: profile, isLoading: profileLoading } = usePublicProfile(userId)
  const { data: packs = [] } = usePublicPacks(userId, packsPage, !!profile)
  const { data: articles = [] } = usePublicArticles(userId, articlesPage, !!profile)

  if (profileLoading) {
    return (
      <MobileScreen>
        <div className="flex flex-1 items-center justify-center">
          <Spinner className="text-gray-400" />
        </div>
      </MobileScreen>
    )
  }

  if (!profile) {
    return <NotFoundView reason="user_not_found" />
  }

  return (
    <MobileScreen bg="white">
      <div data-testid="OhKgp" className="flex flex-1 flex-col">
        <TopBar
          fallback="/"
          title="个人主页"
          right={
            <button
              type="button"
              data-testid="OhKgp-more-report"
              aria-label="举报此用户"
              onClick={() => {
                if (!isAuth) {
                  navigate(`/auth/login?next=${encodeURIComponent(`/u/${profile.user_id}`)}`)
                  return
                }
                setReportOpen(true)
              }}
              className="flex h-9 w-9 items-center justify-center rounded-full bg-[#F6F7F8]"
            >
              <svg width="4" height="16" viewBox="0 0 4 16" fill="#1A1A1A">
                <circle cx="2" cy="2" r="1.5" />
                <circle cx="2" cy="8" r="1.5" />
                <circle cx="2" cy="14" r="1.5" />
              </svg>
            </button>
          }
        />

        <section className="mx-6 mt-2">
          <div className="flex items-center gap-4">
            <span className="flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-full bg-gray-100 text-xl font-semibold text-gray-500">
              {profile.avatar_url ? (
                <img src={resourceUrl(profile.avatar_url)} alt="" className="h-full w-full object-cover" />
              ) : (
                (profile.nickname ?? profile.user_id).slice(0, 1).toUpperCase()
              )}
            </span>
            <div className="min-w-0 flex-1">
              <p className="truncate text-[16px] font-semibold text-[#1A1A1A]">
                {profile.nickname}
              </p>
              {profile.bio && (
                <p className="mt-0.5 whitespace-pre-line text-[12px] leading-[1.5] text-[#9CA3AF]">
                  {profile.bio}
                </p>
              )}
            </div>
          </div>

          <ul className="mt-4 grid grid-cols-3 gap-2 rounded-2xl bg-[#F6F7F8] px-4 py-4 text-center">
            <li>
              <p className="text-[16px] font-semibold tabular-nums text-[#1A1A1A]">
                {profile.public_pack_count}
              </p>
              <p className="text-[11px] text-[#9CA3AF]">公开包</p>
            </li>
            <li>
              <p className="text-[16px] font-semibold tabular-nums text-[#1A1A1A]">
                {profile.published_article_count}
              </p>
              <p className="text-[11px] text-[#9CA3AF]">文章</p>
            </li>
            <li>
              <p className="text-[16px] font-semibold tabular-nums text-[#1A1A1A]">
                {profile.subscriber_count}
              </p>
              <p className="text-[11px] text-[#9CA3AF]">订阅数</p>
            </li>
          </ul>
        </section>

        <nav className="mt-3 flex gap-2 px-6">
          <button
            type="button"
            data-testid="k7UlL"
            onClick={() => setTab('packs')}
            className={cn(
              'flex-1 rounded-full py-2 text-[13px] font-medium',
              tab === 'packs' ? 'bg-[#1A1A1A] text-white' : 'bg-[#F5F6F8] text-[#9CA3AF]',
            )}
          >
            公开资源包 ({profile.public_pack_count})
          </button>
          <button
            type="button"
            data-testid="nJtWU"
            onClick={() => setTab('articles')}
            className={cn(
              'flex-1 rounded-full py-2 text-[13px] font-medium',
              tab === 'articles' ? 'bg-[#1A1A1A] text-white' : 'bg-[#F5F6F8] text-[#9CA3AF]',
            )}
          >
            文章 ({profile.published_article_count})
          </button>
        </nav>

        <div className="flex-1 px-6 pb-24 pt-4">
          {tab === 'packs' ? (
            packs.length === 0 ? (
              <EmptyState text="ta 还没有公开的资源包" />
            ) : (
              <>
                <ul className="grid grid-cols-2 gap-3">
                  {packs.map((p, i) => (
                    <li key={p.id}>
                      <button
                        type="button"
                        data-testid={
                          i === 0 ? 'D7QSf' : i === 1 ? 'GLtfr' : i === 2 ? '0lbpU' : 'a2iLb'
                        }
                        onClick={() => navigate(`/pack/${p.id}`)}
                        className="flex w-full flex-col items-stretch overflow-hidden rounded-xl border border-[#F1F2F4] bg-white text-left"
                      >
                        <span className="flex aspect-[4/3] w-full items-center justify-center bg-[#F6F7F8]">
                          {p.cover_thumb_url_feed ? (
                            <img
                              src={resourceUrl(p.cover_thumb_url_feed)}
                              alt=""
                              className="h-full w-full object-cover"
                            />
                          ) : (
                            <Package size={28} className="text-[#C0C4CC]" aria-hidden="true" />
                          )}
                        </span>
                        <span className="px-3 py-2">
                          <span className="block truncate text-[13px] font-medium text-[#1A1A1A]">
                            {p.name}
                          </span>
                          <span className="mt-1 flex items-center justify-between text-[11px]">
                            <span className="font-semibold text-[#1A1A1A]">
                              {p.price_coin && p.price_coin > 0 ? `${p.price_coin} 金币` : '免费'}
                            </span>
                            <span className="flex items-center gap-0.5 text-[#9CA3AF]">
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.6l-1-1a5.5 5.5 0 0 0-7.8 7.8l1 1L12 21l7.8-7.6 1-1a5.5 5.5 0 0 0 0-7.8z" />
                              </svg>
                              {p.like_count}
                            </span>
                          </span>
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
                {profile.public_pack_count > PUBLIC_LIST_PAGE_SIZE && (
                  <Pagination
                    page={packsPage}
                    total={profile.public_pack_count}
                    pageSize={PUBLIC_LIST_PAGE_SIZE}
                    onChange={(p) => {
                      setPacksPage(p)
                      scrollListTop()
                    }}
                  />
                )}
              </>
            )
          ) : articles.length === 0 ? (
            <EmptyState text="ta 还没有发布的文章" />
          ) : (
            <>
              <div className="columns-2 gap-3">
                {articles.map((a) => (
                  <ArticleCard
                    key={a.id}
                    article={a}
                    hideAuthor
                    testId="OhKgp-article-card"
                    onClick={() => navigate(`/article/${a.id}`)}
                  />
                ))}
              </div>
              {profile.published_article_count > PUBLIC_LIST_PAGE_SIZE && (
                <Pagination
                  page={articlesPage}
                  total={profile.published_article_count}
                  pageSize={PUBLIC_LIST_PAGE_SIZE}
                  onChange={(p) => {
                    setArticlesPage(p)
                    scrollListTop()
                  }}
                />
              )}
            </>
          )}
        </div>
      </div>

      {/* D6 DY9nl 举报 modal */}
      <ReportModal
        open={reportOpen}
        onClose={() => setReportOpen(false)}
        targetUserId={profile.user_id}
        targetUsername={profile.username ?? undefined}
      />
    </MobileScreen>
  )
}

function EmptyState({ text }: { text: string }) {
  return (
    <div className="flex flex-col items-center gap-2 py-12 text-[13px] text-[#9CA3AF]">
      <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
        <rect x="4" y="6" width="16" height="14" rx="2" />
        <path d="M8 10h8M8 14h5" />
      </svg>
      <p>{text}</p>
    </div>
  )
}
